import "@app/App.css";
import { GridCell, NEW_ROW_ID } from "@app/grid/GridCell";
import { Button } from "@comps/ui/button";
import { TableCell, TableRow } from "@comps/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { DeleteRightOption, ObjectRightType, ViewRightType, hasGlobalRightOption } from "@dto/Users";
import { FieldType, TYPE_ITEMS_FIELD_KEY, TYPE_ITEMS_OBJECT_KEY } from "@dto/fields";
import { DeleteRecordRequest, FieldColumn, Item, ItemKey, Line, MetaLine, NewRecordRequest, Record, SingleFilter } from "@dto/records";
import { Group } from "@dto/views";
import { GRAY_600_COLOR, IsDarkColor, applyBrightness } from "@utils/colors";
import { BASE_URL, PATH_SEPARATOR, SELECT_COLUMN_WIDTH, SOURCE_SEPARATOR } from "@utils/constants";
import { getRecordKeys } from "@utils/data";
import { allowedToAddRecord, cleanFilterFromCondition, cleanGroupsFromCondition, getDirectPathFromFiltersAndGroups, getFilterValues } from "@utils/filters";
import { getFieldByPathKeys } from "@utils/model";
import { toastError } from "@utils/toasts";
import { TW_AddRecordButtonIcon, TW_AddRecordTableRowButton, TW_DataTableBorder, useHover } from "@utils/twClasses";
import { del, post } from "@utils/utils";
import { getUndoSessionId } from "@utils/session";
import { UndoLabelCode, useUndoStore } from "@utils/undo";
import { Check, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate, translateHtml } from "src/i18n/i18n";
import { useDataStore, useModelStore, useUserStore } from "src/store";


interface AddRecordTableRowProps {
  values: Map<string, any>
  fieldByName: boolean
  className?: string
  style?: React.CSSProperties
  displayText?: boolean
  disabled?: boolean
  tooltip?: string,
  lineHovered?: boolean
  fieldColumns: FieldColumn[]
  pinIndex: number,
  fieldColumnStarts: number[],
  onEditionStart: () => void,
  onEditionEnd: () => void,
}

function AddRecordTableRow({ values, fieldByName, className, style, displayText, disabled = false, tooltip = undefined, lineHovered = undefined, fieldColumns, pinIndex, fieldColumnStarts, onEditionStart, onEditionEnd }: AddRecordTableRowProps) {

  const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
  const selectedView = useModelStore(state => state.selectedView);
  const workspace = useModelStore(state => state.selectedWorkspace);
  const wsColor = useModelStore(state => state.wsColor);
  const bgColor = applyBrightness(wsColor, 25);
  const textColor = IsDarkColor(bgColor) ? "white" : "black";
  const useButtonHover = useHover({ backgroundColor: "white", color: GRAY_600_COLOR, ...style }, { backgroundColor: bgColor, color: textColor, ...style }, lineHovered);
  const objectInfo = useModelStore(state => state.selectedObjectInfos);
  const { t } = useTranslation();
  const [isAddingRecord, setIsAddingRecord] = useState(false);
  const enableRowActions = useDataStore(state => state.enableRowActions);
  const edit = useDataStore(state => state.edit);
  const [newRecord, setNewRecord] = useState<Record | null>(null);
  const metaLines = useDataStore(state => state.metaLines);
  const updateMetaLines = useDataStore(state => state.updateMetaLines);
  const [metaLine, setMetaLine] = useState<MetaLine | undefined>(undefined);
  const user = useUserStore(state => state.user);
  const userRole = useModelStore(state => state.userRole);

  const fetchData = useDataStore(state => state.fetchData);
  const insertNewRecord = useDataStore(state => state.insertNewRecord);
  const relationByKeys = useModelStore(state => state.relationByKeys);
  const fieldByKeys = useModelStore(state => state.fieldByKeys);
  const fieldsByPathKeys = getFieldByPathKeys(objectInfo, objectInfo?.fields ?? null, relationByKeys, fieldByKeys);
  const fetchLine = useDataStore(state => state.fetchLine);

  const hasDeleteAccessOnObject = hasGlobalRightOption(user, userRole, ObjectRightType, DeleteRightOption, selectedObjectInfos?.object?._key);
  const hasDeleteAccessOnView = hasGlobalRightOption(user, userRole, ViewRightType, DeleteRightOption, selectedView?._key);
  const hasDeleteAccess = hasDeleteAccessOnObject && hasDeleteAccessOnView;

  useEffect(() => {
    setMetaLine(metaLines.get(NEW_ROW_ID) ?? undefined);
  }, [metaLines]);

  const directPathFromFiltersAndGroups = getDirectPathFromFiltersAndGroups(selectedView?.filter, selectedView?.groups ?? null);


  const pathCleanningCondition = (path: string) => {
    const paramSrcsParts = path.split(SOURCE_SEPARATOR);
    if (paramSrcsParts.length !== 2) {
      return false;
    }
    const paramPath = paramSrcsParts[1];
    for (const pathKey of directPathFromFiltersAndGroups) {
      if (path === pathKey) {
        return true;
      }
      const srcsParts = pathKey.split(SOURCE_SEPARATOR);
      if (srcsParts.length !== 2) {
        continue;
      }
      const pathPath = srcsParts[1];
      const pathParts = pathPath.split(PATH_SEPARATOR);
      if (pathParts.length !== 2) {
        continue;
      }
      const linkPart = pathParts[0];
      if (paramPath.startsWith(linkPart)) {
        return false;
      }
    }
    return true;
  }

  const filterCleanningCondition = (singleFilter: SingleFilter) => {
    if (singleFilter.value != null && singleFilter.valueKey != null) {
      return false;
    }
    const pathKey = singleFilter.pathKey;
    return pathCleanningCondition(pathKey);
  }

  const groupsCleanningCondition = (group: Group) => {
    const pathKey = group.pathKey;
    if (!pathKey) {
      return false;
    }
    return pathCleanningCondition(pathKey);
  }

  const cleanedFilter = cleanFilterFromCondition(selectedView?.filter ?? null, filterCleanningCondition);
  const cleanedGroups = cleanGroupsFromCondition(selectedView?.groups ?? null, groupsCleanningCondition);

  const doFilterAllowedCreation = allowedToAddRecord(cleanedFilter, fieldsByPathKeys, cleanedGroups, selectedView?.aggregations ?? new Map(), fieldByKeys);

  const isDisabled = disabled || !doFilterAllowedCreation;


  const addRecord = () => {
    if (!workspace || !objectInfo) {
      console.error("No workspace or object selected");
      return;
    }
    const newValues = new Map<string, any>(values);

    let overridenLinkByValue: Map<string, boolean> | undefined;
    if (selectedView?.filter) {
      const filterValues = getFilterValues(cleanedFilter, fieldsByPathKeys, fieldByKeys);
      const values = filterValues.filterValues;
      overridenLinkByValue = filterValues.overridenLinkByValue;
      if (values.size > 0) {
        values.forEach((value, key) => {
          newValues.set(key, value);
        });
      }
    }

    const newRecordRequest: NewRecordRequest = {
      workspaceKey: workspace._key,
      objectKey: objectInfo.object._key,
      fieldValues: newValues,
      fieldByName: fieldByName,
      linkByValue: false,
      overridenLinkByValue: overridenLinkByValue,
      sessionId: getUndoSessionId(),
    }
    post<Record>(`${BASE_URL}/data/records`, newRecordRequest).then(response => {
      if (isCompozzError(response)) {
        const cError = response as CompozzError;
        if (cError.i18n) {
          toastError(translate(t, cError.i18n, cError.params));
        } else {
          toastError(cError.message);
        }
      } else {
        const newRecord = response as Record;
        useUndoStore.getState().markUndoable(objectInfo.object._key, UndoLabelCode.create, 1);
        setIsAddingRecord(true);
        enableRowActions(false);
        onEditionStart();
        setNewRecord(newRecord);
        edit({ rowId: NEW_ROW_ID, column: 0 });
        const newLine: Line = {
          items: fieldColumns.map(fieldColumn => {
            let parentKey = null;
            let itemKey = { objectKey: objectInfo.object._key, recordKey: newRecord._key, fieldKey: fieldColumn.field._key } as ItemKey;
            if (fieldColumn.srcField) {
              parentKey = { objectKey: objectInfo.object._key, recordKey: newRecord._key };
              const objectKey = fieldColumn.srcField.typeItems?.get(TYPE_ITEMS_OBJECT_KEY) || "";
              const fieldKey = fieldColumn.srcField.typeItems?.get(TYPE_ITEMS_FIELD_KEY) || "";
              itemKey = { objectKey: objectKey, fieldKey: fieldKey, recordKey: "" };
            }

            const value = (!fieldColumn.srcField && !fieldColumn.isPivot && fieldColumn.field.type !== FieldType.Link)
              ? (newValues.get(fieldColumn.field._key) ?? (fieldByName ? newValues.get(fieldColumn.field.name) : null) ?? null)
              : null;
            return ({
              key: itemKey,
              value: value,
              readOnly: fieldColumn.field.readOnly,
              aggregated: false,
              parentKey: parentKey
            })
          }),
          children: null,
          isLeftGrouped: false
        }
        const newMetaLine: MetaLine = {
          id: NEW_ROW_ID,
          line: newLine,
          parentId: null,
          childrenIds: [],
          level: 0
        }
        const newMetaLines = new Map<string, MetaLine>(metaLines);
        newMetaLines.set(NEW_ROW_ID, newMetaLine);
        updateMetaLines(newMetaLines);
        const recordKeys = getRecordKeys(newMetaLine, objectInfo.object._key);
        if (recordKeys.length > 0) {
          fetchLine(NEW_ROW_ID, recordKeys, selectedView);
        }
      }
    })
  }

  const validateAddingRecord = () => {
    setIsAddingRecord(false);
    setNewRecord(null);
    insertNewRecord(NEW_ROW_ID);
    enableRowActions(true);
    onEditionEnd();
    setTimeout(() => fetchData(), 0);
  }

  const cancelAddingRecord = () => {
    if (!metaLine || !objectInfo || !workspace || !hasDeleteAccess) {
      return;
    }
    const recordKeys = getRecordKeys(metaLine, objectInfo.object._key);
    if (recordKeys.length > 0) {
      const request: DeleteRecordRequest = {
        workspaceKey: workspace._key,
        objectKey: objectInfo.object._key,
        recordKeys: recordKeys
      }
      del(`${BASE_URL}/data/records`, request).then(response => {
        // No response means success
        if (!response) {
          setIsAddingRecord(false);
          setNewRecord(null);
          enableRowActions(true);
          onEditionEnd();
          fetchData();
        } else {
          console.error("Error deleting records");
          if (isCompozzError(response)) {
            const cError = response as CompozzError;
            if (cError.i18n) {
              toastError(translate(t, cError.i18n, cError.params));
            } else {
              toastError(cError.message);
            }
          } else {
            toastError(t("record.delete.error"));
          }
        }
      })
    }
  }

  const addingLeftCellStyle = {
    borderTop: "2px solid " + wsColor,
    borderBottom: "2px solid " + wsColor,
    borderLeft: "2px solid " + wsColor,
    borderRight: "1px solid " + "var(--TABLE_BORDER)",
    backgroundColor: "white",
    borderRadius: "0.25rem 0 0 0.25rem",
    position: "sticky",
    left: "0",
    zIndex: 15
  } as React.CSSProperties;

  const addingRightCellStyle = {
    borderTop: "2px solid " + wsColor,
    borderBottom: "2px solid " + wsColor,
    borderLeft: "1px solid " + "var(--TABLE_BORDER)",
    borderRight: "2px solid " + wsColor,
    backgroundColor: "white",
    borderRadius: "0 0.25rem 0.25rem 0",
  }

  const addingCellStyle = {
    borderTop: "2px solid " + wsColor,
    borderBottom: "2px solid " + wsColor,
    borderLeft: "1px solid " + "var(--TABLE_BORDER)",
    borderRight: "1px solid " + "var(--TABLE_BORDER)",
    backgroundColor: "white",
  }

  function getColumnStyle(curIndex: number, pinIndex: number, nbColumns: number): React.CSSProperties {
    const isPinned = curIndex <= pinIndex;
    const left = fieldColumnStarts[curIndex] ?? SELECT_COLUMN_WIDTH;
    let zIndex = 5;
    if (isPinned) {
      zIndex = 7;
    }
    let borderStyle = (curIndex < nbColumns - 1) ? addingCellStyle : addingRightCellStyle
    if (curIndex === pinIndex) {
      borderStyle = {
        ...borderStyle,
        borderRight: "1px solid " + GRAY_600_COLOR,
      }
    }
    return {
      position: isPinned ? 'sticky' : 'relative',
      left: isPinned ? `${left}px` : undefined,
      zIndex: zIndex,
      ...borderStyle
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (!isAddingRecord) {
      return;
    }
    if (event.key === "Escape") {
      cancelAddingRecord();
    } else if (event.key === "Enter" && event.ctrlKey) {
      validateAddingRecord();
      if (event.shiftKey) {
        setTimeout(() => {
          addRecord();
        }, 100);
      }
    }
  }

  const addRecordButton = (
    <>
      {(isDisabled || tooltip) && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger style={style}>
              <Button className={TW_AddRecordTableRowButton + (className ? " " + className : "")} onClick={addRecord} disabled={isDisabled} {...useButtonHover}>
                <Plus className={TW_AddRecordButtonIcon + (useButtonHover.isHovered ? " text-black" : " text-gray-400")} />{displayText && t("data.record.add.button")}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {tooltip ? tooltip : t("data.record.add.button.disabled")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
      }
      {!isDisabled && !tooltip && (
        <Button className={TW_AddRecordTableRowButton + (className ? " " + className : "")} onClick={addRecord} disabled={isDisabled} {...useButtonHover}>
          <Plus className={TW_AddRecordButtonIcon + (useButtonHover.isHovered ? " text-black" : " text-gray-400")} />{displayText && t("data.record.add.button")}
        </Button>
      )}
    </>
  )


  return (
    <TableRow className={"sticky bottom-0 bg-transparent hover:bg-transparent h-12"} style={{ zIndex: 20 }} onKeyDown={handleKeyDown}>
      {(!isAddingRecord) &&
        <TableCell className="sticky left-2">
          {addRecordButton}
        </TableCell>
      }
      {(isAddingRecord) &&
        <>

          <TableCell colSpan={1} style={addingLeftCellStyle} className="p-0">
          <div className="absolute -top-8 left-0 flex flex-row justify-start items-center gap-1">
            <TooltipProvider delayDuration={1000}>
              <Tooltip>
                <TooltipTrigger asChild>
                    <div className="bg-white rounded-lg p-1 cursor-pointer border-l-1 border-t-1 border-r-1 border-b-1" style={{ borderColor: wsColor }} onClick={validateAddingRecord}>
                      <p className="text-xs text-left font-bold" style={{ color: wsColor }}>{t("data.record.add.validate.button")}</p>
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                  {translateHtml(t, "data.record.add.validate.tooltip")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {hasDeleteAccess && (
              <TooltipProvider delayDuration={1000}>
              <Tooltip>
                <TooltipTrigger asChild>
                    <div className="bg-white rounded-lg p-1 cursor-pointer border-l-1 border-t-1 border-r-1 border-b-1" style={{ borderColor: wsColor }} onClick={cancelAddingRecord}>
                      <p className="text-xs text-left font-bold" style={{ color: wsColor }}>{t("data.record.add.cancel.button")}</p>
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                  {translateHtml(t, "data.record.add.cancel.tooltip")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            )}
            </div>
            <div className="flex flex-col justify-start items-center gap-0.5">
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger>
                    <Check className="cursor-pointer h-4 w-4" style={{ color: wsColor }} onClick={validateAddingRecord} />
                  </TooltipTrigger>
                  <TooltipContent>
                    {translateHtml(t, "data.record.add.validate.tooltip")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </TableCell>
          {fieldColumns.map((fieldColumn, index) => {
            let borderClass = TW_DataTableBorder;
            borderClass += " border-l-0 ";
            borderClass += " overflow-hidden text-ellipsis text-nowrap whitespace-nowrap p-0";

            const position = { rowId: NEW_ROW_ID, column: index };
            let itemKey = fieldColumn.isPivot ? { objectKey: objectInfo?.object._key || "", fieldKey: fieldColumn.field._key } as ItemKey : { objectKey: objectInfo?.object._key || "", fieldKey: fieldColumn.field._key, recordKey: newRecord?._key || "" } as ItemKey
            let parentKey = null;
            const srcField = fieldColumn.srcField;
            if (srcField && srcField.type === FieldType.Link) {
              parentKey = { objectKey: objectInfo?.object._key || "", recordKey: newRecord?._key || "" };
              const objectKey = srcField.typeItems?.get(TYPE_ITEMS_OBJECT_KEY) || "";
              const fieldKey = srcField.typeItems?.get(TYPE_ITEMS_FIELD_KEY) || "";
              const recordKey = "";
              itemKey = { objectKey: objectKey, fieldKey: fieldKey, recordKey: recordKey };
            }
            let readOnly = false;
            if (fieldColumn.field.readOnly === true || (fieldColumn.field.formula && fieldColumn.field.formula !== "")) {
              readOnly = true;
            }
            let item: Item = { key: itemKey as ItemKey, value: null, readOnly: readOnly, aggregated: false, parentKey: parentKey };
            if (metaLine && metaLine.line.items.length > 0) {
              item = metaLine.line.items[index];
            }
            return (
              <TableCell key={"adding-record-table-row-cell-" + fieldColumn.field._key + "-" + index} className={borderClass} style={getColumnStyle(index, pinIndex, fieldColumns.length)} >
                <GridCell
                  key={"grid-cell-new-record-" + fieldColumn.field._key + "-" + index}
                  fieldColumn={fieldColumn}
                  item={item}
                  position={position}
                  noDataFetch={true}
                />
              </TableCell>
            )
          })}
        </>
      }
    </TableRow>
  )


}

export default AddRecordTableRow;

